import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	OnDestroy,
	ChangeDetectorRef,
} from '@angular/core';
import { environment } from '@environment/environment';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LoadingSpinnerDialogComponent } from '@shared/loading-spinner-dialog/loading-spinner-dialog.component';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { PRICING_PREVIEW } from '@domain/constants/pricing-preview';
import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import {
	GET_UNIX_TIMESTAMP,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	POPULATE_FOR_THIS_ACCOUNT_ATTRIBUTE,
	RestService,
	TransactionDTO,
	IS_NULL_OR_UNDEFINED,
	DIFFERENCE_IN_DAYS,
	DATE_YMD,
	TO_DOLLARS,
} from 'cargo-chief-commons';
import { CurrencyPipe } from '@angular/common';
import { AddPriceReviewDialogComponent } from '../add-price-review-dialog/add-price-review-modal';
import { catchError, finalize, timeout, takeUntil } from 'rxjs/operators';
import { of, BehaviorSubject } from 'rxjs';
import { RecentTransactionsDialogComponent } from './../recent-transactions-dialog/recent-transactions-dialog';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { PricingFeedbackService } from '@service/pricingFeedback/pricing-feedback.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SORT_BY_DATE } from '@domain/constants/functions';
import { RECENT_TRANSACTION_SORT } from '@domain/constants/app-variables';
import { PRICING_ERROR } from '@domain/constants/error';
import { ProductsService } from '@service/products/products.service';
import { PricingHelperService } from '@service/pricing-helper/pricing-helper.service';
import { RecentTransactionsCount } from '@domain/interface/recent-transactions-count';
import { BaseComponent } from '@shared/base/base.component';

// CN-8810 / CN-8851: the countries mercury has no pricing coverage for. The value
// doubles as the adjective in the "not available for ... lanes" message.
export type UnsupportedPricingCountry = 'Canadian' | 'Mexican';

export interface GreenScreenRate {
	confidenceScore: number;
	distance: number;
	fuelRate: number;
	highBuyRate: number;
	lowBuyRate: number;
	startBuyRate: number;
	targetBuyRate: number;
}

// One Sonar rate. `targetBuyRate` is the median and low/high bound the 33rd-67th
// percentile band; they are whole-lane totals, not per-mile figures.
export interface SonarLaneRate {
	confidenceScore: number;
	dataTimestamp: string;
	distance: number;
	fuelRate: number;
	highBuyRate: number;
	lowBuyRate: number;
	ratePerMile: number;
	targetBuyRate: number;
}

// Sonar publishes spot for the contiguous US only and contract adds Canada, so either side
// can be absent on its own -- a Canadian lane legitimately has a contract rate and no spot
// rate. Mercury answers 404 when it has neither.
export interface SonarRate {
	contract: SonarLaneRate;
	spot: SonarLaneRate;
}

@Component({
	selector: 'app-pricing',
	templateUrl: './pricing.component.html',
	styleUrls: ['./pricing.component.scss'],
	providers: [CurrencyPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() searchTruckForm: any = null;
	@Input() truckLaneSearchId: string = null;
	@Input() truckSearchId: string = null;
	@Input() inOpportunitiesPage: boolean = false;
	@Input() v1PricingLayout: boolean = true;
	@Input() greenScreensEnabled: boolean = false;
	// Triumph and Sonar are alternatives -- an account has one or the other -- and
	// they share the integration column of the rate row. Kept as two inputs rather than one
	// because the column's branding, its figures and the rate's own shape all differ.
	@Input() sonarEnabled: boolean = false;
	@Output() lanePricingCompleted = new EventEmitter<any>();
	@Output() pricingFeedbackSubmitted = new EventEmitter<any>();
	searchTruckFormCopy: any = null;
	SEGMENT_EVENTS = SegmentEventsConstants;

	recentTransactions: Array<TransactionDTO> = null;
	lanePricing: any = null;

	environment = environment;

	showCurrentRatesCard: boolean = false;
	showRecentTransactionsCard: boolean = true;
	showGreenScreensCard: boolean = false;
	showSonarCard: boolean = false;
	rateFeedbackProvided = false;
	loadsUsed: string = '';
	accountId: number = null;
	priceNotFound = false;
	integrationPriceNotFound = false;
	searchRadius = null;
	// CN-8810 / CN-8851: set when the lane's country has no pricing coverage in
	// this environment, null when pricing may proceed. Drives the "not available
	// for ... lanes" message.
	unsupportedPricingCountry: UnsupportedPricingCountry = null;

	totalTransactions!: number;
	numberOfPastDaysTransaction!: number;

	isRecentTransactionsRequestInProgress: boolean = false;
	isRecentTransactionsCountRequestInProgress: boolean = false;
	greenScreenRate: GreenScreenRate = null;
	sonarRate: SonarRate = null;
	agedTransactionCount: boolean = false;

	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	TO_DOLLARS = TO_DOLLARS;
	CONFIDENCE_METER_CONTAINER = '85px';

	@ViewChild('perMileEndContaier') perMileEndContaier: ElementRef;
	@ViewChild('perMileEndTextContainer') perMileEndTextContainer: ElementRef;
	@ViewChild('recentTransactionsLink') recentTransactionsLink: ElementRef;
	@ViewChild('allInPriceContainer', { read: ElementRef }) allInPriceContainer: ElementRef;
	@ViewChild('allInPriceEnd') allInPriceEnd: ElementRef;
	recentTransactionsCount!: RecentTransactionsCount;

	// variables for component state
	PRODUCT_PRICING: boolean = false;
	isCitySelectedInSearchTruckForm: boolean = false;
	isEquipmentSelectedInSearchTruckForm: boolean = false;

	isLanePricingRequestInProgress$ = new BehaviorSubject<boolean>(false);
	isGreenScreenRequestInProgress$ = new BehaviorSubject<boolean>(false);
	isSonarRequestInProgress$ = new BehaviorSubject<boolean>(false);
	isC4RateSetFromIntegrationData$ = new BehaviorSubject<boolean>(false);

	constructor(
		public dialog: MatDialog,
		public utilService: UtilService,
		private urlService: UrlService,
		public cargochief: Cargochief,
		private restService: RestService,
		public accountSettings: AccountSettingsService,
		private pricingFeedbackService: PricingFeedbackService,
		public productsService: ProductsService,
		public pricingHelperService: PricingHelperService,
		private cdRef: ChangeDetectorRef
	) {
		super();
		this.accountId = this.cargochief.ACCOUNT_ID;
	}

	ngOnInit() {
		this.PRODUCT_PRICING = this.productsService.PRICING;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			IS_NOT_NULL_OR_UNDEFINED(changes.searchTruckForm) &&
			IS_NOT_NULL_OR_UNDEFINED(this.searchTruckForm)
		) {
			this.isCitySelectedInSearchTruckForm = this.pricingHelperService.isCitySelected(
				this.searchTruckForm
			);
			this.isEquipmentSelectedInSearchTruckForm = this.pricingHelperService.isEquipmentSelected(
				this.searchTruckForm
			);
			if (this.isCitySelectedInSearchTruckForm && this.isEquipmentSelectedInSearchTruckForm) {
				this.searchTruckFormCopy = Object.assign({}, this.searchTruckForm);
				this.rateFeedbackProvided = false;
				if (
					this.productsService.PRICING &&
					this.pricingHelperService.hasSearchChanged(
						changes.searchTruckForm.previousValue,
						changes.searchTruckForm.currentValue
					)
				) {
					this.searchRadius = null;
					this.loadsUsed = '';
					const unsupportedCountry = this.getUnsupportedPricingCountry();
					if (unsupportedCountry) {
						this.skipPricingForUnsupportedCountry(unsupportedCountry);
					} else {
						this.unsupportedPricingCountry = null;
						this.getLanePricing();
						this.getRecentTransactionsCount();
						// else-if, matching skipPricingForUnsupportedCountry and the template: the
						// row has one integration column and Triumph holds it, so firing both
						// would spend a request on a rate nothing renders and let the loser's
						// failure handler clear integrationPriceNotFound for the winner.
						if (this.greenScreensEnabled) {
							this.getGreenScreensLanePricing();
						} else if (this.sonarEnabled) {
							this.getSonarLanePricing();
						}
					}
				}
			} else {
				this.unsupportedPricingCountry = null;
				this.showRecentTransactionsCard = false;
				this.showCurrentRatesCard = false;
				this.showGreenScreensCard = false;
				this.showSonarCard = false;
				this.priceNotFound = true;
				this.integrationPriceNotFound = true;
			}
		} else if (
			this.pricingHelperService.greenScreensEnabledValueChanged(changes) &&
			this.greenScreensEnabled
		) {
			this.getGreenScreensLanePricing();
		} else if (this.pricingHelperService.sonarEnabledValueChanged(changes) && this.sonarEnabled) {
			this.getSonarLanePricing();
		}
	}

	/**
	 * The rate row lays out the same way for either pricing integration -- C4's columns, then
	 * one integration column -- so the layout gates read the pair and only the column itself
	 * branches on which provider is connected. An account has one or the other; with both
	 * connected Triumph wins the column, which is the state the row was built for.
	 */
	get integrationRatesEnabled(): boolean {
		return this.greenScreensEnabled || this.sonarEnabled;
	}

	get showIntegrationRateCard(): boolean {
		return this.showGreenScreensCard || this.showSonarCard;
	}

	// CN-8810 / CN-8851: mercury's LANE_PRICING_VALID_COUNTRIES is ['US'], so a
	// pricing call for a Canadian or Mexican origin/destination is rejected after
	// mercury has already paid for a geocode. Where the country's flag is off we
	// skip the doomed call entirely. Canada wins the ambiguous subdivision codes
	// ('BC', 'NL') by being checked first -- see MEXICAN_STATES.
	getUnsupportedPricingCountry(): UnsupportedPricingCountry {
		if (
			this.pricingHelperService.isCanadianLane(this.searchTruckFormCopy) &&
			!environment.FEATURE_FLAGS.CANADA_LANE_PRICING_ENABLED
		) {
			return 'Canadian';
		}
		if (
			this.pricingHelperService.isMexicanLane(this.searchTruckFormCopy) &&
			!environment.FEATURE_FLAGS.MEXICO_LANE_PRICING_ENABLED
		) {
			return 'Mexican';
		}
		return null;
	}

	// A country whose lane pricing is unsupported may still have transactions to show, so this
	// reads the country switch rather than the pricing flag. Enabling one country never opens
	// the other.
	areRecentTransactionsAvailableForUnsupportedCountry(): boolean {
		if (this.unsupportedPricingCountry === 'Canadian') {
			return environment.FEATURE_FLAGS.CANADA_ENABLED;
		}
		if (this.unsupportedPricingCountry === 'Mexican') {
			return environment.FEATURE_FLAGS.MEXICO_ENABLED;
		}
		return false;
	}

	// Show the "not available for ... lanes" message instead of pricing, while still surfacing
	// recent transactions where that country is enabled.
	skipPricingForUnsupportedCountry(country: UnsupportedPricingCountry) {
		this.unsupportedPricingCountry = country;
		this.priceNotFound = false;
		this.showCurrentRatesCard = false;
		this.lanePricing = null;
		this.lanePricingCompleted.emit(this.lanePricing);
		this.loadsUsed = '';

		// CN-8852: Triumph is a separate provider that prices lanes mercury cannot,
		// so a country mercury rejects is no reason to skip it. Sonar covers Canada on its
		// contract rate for the same reason. Each sets its own card flag and
		// integrationPriceNotFound, including on failure.
		if (this.greenScreensEnabled) {
			this.getGreenScreensLanePricing();
		} else if (this.sonarEnabled) {
			this.getSonarLanePricing();
		} else {
			this.showGreenScreensCard = false;
			this.showSonarCard = false;
			this.integrationPriceNotFound = true;
		}

		if (this.areRecentTransactionsAvailableForUnsupportedCountry()) {
			this.getRecentTransactionsCount();
			if (this.productsService.RECENT_TRANSACTIONS && this.v1PricingLayout) {
				this.getRecentTransactions();
			}
		} else {
			this.showRecentTransactionsCard = false;
			this.recentTransactions = null;
			// Clear the count too: it survives from the previous (supported-country)
			// search, and the empty state keys its link off the count.
			this.recentTransactionsCount = null;
		}
	}

	openPricingPreviewDialog(eventName: string) {
		this.pricingHelperService.fireSegmentEvent(eventName);
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '900px',
			data: {
				title: 'Real Time Pricing with LaneMaster',
				body: PRICING_PREVIEW,
				cancelButtonText: '',
				ConfirmButtonText: 'Request Info',
				showCloseButton: false,
				buttonActionLayout: 'center center',
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (IS_NOT_NULL_OR_UNDEFINED(result) && result) {
				this.signupConfirmed();
				this.pricingHelperService.fireSegmentEvent(
					SegmentEventsConstants.user_registered_for_pricing
				);
			}
		});
	}

	openPriceReviewDialog() {
		this.dialog.open(AddPriceReviewDialogComponent, {
			width: '500px',
		});
	}

	signupConfirmed() {
		const loadingDialog = this.dialog.open(LoadingSpinnerDialogComponent, {
			width: '300px',
		});
		const url = this.urlService.registerForPricingPreview(this.cargochief.USER_ID);
		this.restService.POST(url, {}).subscribe(
			(result) => {
				this.pricingHelperService.emailSentSuccessfully();
				loadingDialog.close();
			},
			(error) => {
				if (error.status == 200) {
					this.pricingHelperService.emailSentSuccessfully();
				} else {
					this.utilService.showSnackbar(
						'Unable to register you now. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
				loadingDialog.close();
			}
		);
	}

	getRecentTransactions({ isSecondCall } = { isSecondCall: false }) {
		this.isRecentTransactionsRequestInProgress = true;
		this.showRecentTransactionsCard = true;
		this.searchTruckFormCopy = {
			...this.searchTruckFormCopy,
			originDeadhead: this.searchRadius || 100,
			destinationDeadhead: this.searchRadius || 100,
		};
		if (isSecondCall) {
			this.searchTruckFormCopy = {
				...this.searchTruckFormCopy,
				age: environment.RECENT_TRANSACTION_AGE,
			};
		} else {
			this.recentTransactions = null;
		}
		const url = this.urlService.getRecentTransactions(0, 5);
		this.restService
			.POST(url, this.searchTruckFormCopy)
			.pipe(
				finalize(() => {
					this.isRecentTransactionsRequestInProgress = false;
				})
			)
			.subscribe(
				(result) => {
					this.recentTransactions = SORT_BY_DATE(
						POPULATE_FOR_THIS_ACCOUNT_ATTRIBUTE(result.data, this.accountId),
						RECENT_TRANSACTION_SORT.PICKUP_DATE
					);
					if (result.data.length) {
						this.totalTransactions = result.data.totalElements || result.data.length;
						this.numberOfPastDaysTransaction = Math.abs(
							DIFFERENCE_IN_DAYS(
								DATE_YMD(new Date()),
								DATE_YMD(result.data[result.data.length - 1].pickupDate)
							)
						);
					} else {
						this.totalTransactions = null;
						this.numberOfPastDaysTransaction = null;
					}
					if (this.recentTransactions.length == 0) {
						if (isSecondCall) {
							this.showRecentTransactionsCard = false;
						} else {
							this.getRecentTransactions({ isSecondCall: true });
						}
					}
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.user_viewed_recent_transactions,
						'response',
						this.recentTransactions,
						null,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm,
						null,
						this.recentTransactions.length
					);
				},
				(error) => {
					let errorMessage = PRICING_ERROR.RECENT_TRANSACTION.DEFAULT;
					if (error.status === 422) {
						errorMessage = error.error;
					}
					this.utilService.showSnackbar(errorMessage, SNACKBAR.WARNING, SNACKBAR.ACTION);
					this.showRecentTransactionsCard = false;
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.recent_transactions_failed_for_search,
						'response',
						this.recentTransactions,
						null,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm
					);
				}
			);
	}

	getLanePricing() {
		this.isLanePricingRequestInProgress$.next(true);
		this.showCurrentRatesCard = true;
		this.lanePricing = null;
		this.lanePricingCompleted.emit(this.lanePricing);
		this.priceNotFound = false;
		const url = this.urlService.getLanePricing();
		this.restService
			.POST(url, this.searchTruckFormCopy)
			.pipe(
				takeUntil(this.isDestroyed$),
				timeout(120000),
				finalize(() => {
					this.isLanePricingRequestInProgress$.next(false);
					if (this.productsService.RECENT_TRANSACTIONS && this.v1PricingLayout) {
						this.getRecentTransactions();
					}
				}),
				catchError((error: HttpErrorResponse) => {
					let errorMessage = PRICING_ERROR.LANE_PRICING.DEFAULT;
					if (error.status === 404) {
						errorMessage = PRICING_ERROR.LANE_PRICING.ERR_404;
						this.priceNotFound = true;
					} else if (error.status === 422) {
						errorMessage = error.error;
						this.showCurrentRatesCard = false;
					} else {
						this.showCurrentRatesCard = false;
					}
					this.utilService.showSnackbar(errorMessage, SNACKBAR.WARNING, SNACKBAR.ACTION);
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.current_price_failed_for_search,
						'lanePricing',
						this.lanePricing,
						null,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm
					);
					return of(null);
				})
			)
			.subscribe(
				(result) => {
					if (IS_NOT_NULL_OR_UNDEFINED(result) && IS_NOT_NULL_OR_UNDEFINED(result.data)) {
						this.lanePricing = result.data;
						this.searchRadius = result.data.searchRadius || 100;
						this.lanePricingCompleted.emit(this.lanePricing);
						if (
							IS_NULL_OR_UNDEFINED(this.lanePricing.loadsUsed) ||
							(IS_NOT_NULL_OR_UNDEFINED(this.lanePricing.loadsUsed) &&
								this.lanePricing.loadsUsed == 0) ||
							(IS_NOT_NULL_OR_UNDEFINED(this.lanePricing.loadsUsed) &&
								this.lanePricing.loadsUsed > 5000)
						) {
							this.loadsUsed = '> 5000';
						} else {
							this.loadsUsed = String(this.lanePricing.loadsUsed);
						}
					}
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.user_viewed_current_price,
						'lanePricing',
						this.lanePricing,
						null,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm
					);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to load Current Rates.',
						SNACKBAR.WARNING,
						SNACKBAR.ACTION
					);
					this.showCurrentRatesCard = false;
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.current_price_failed_for_search,
						'lanePricing',
						this.lanePricing,
						null,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm
					);
					this.setC4RateFromGsPricing();
				}
			);
	}

	getGreenScreensLanePricing() {
		this.greenScreenRate = null;
		this.isGreenScreenRequestInProgress$.next(true);
		this.isC4RateSetFromIntegrationData$.next(false);
		this.showGreenScreensCard = true;
		this.integrationPriceNotFound = false;
		const url = this.urlService.getGreenScreensLanePricing();
		const searchTruckForm = this.pricingHelperService.retainSingleEquipment(
			this.searchTruckFormCopy
		);
		this.restService
			.POST(url, searchTruckForm)
			.pipe(
				finalize(() => {
					this.isGreenScreenRequestInProgress$.next(false);
					this.cdRef.detectChanges();
				})
			)
			.subscribe(
				(result) => {
					this.greenScreenRate = result['data'];
					this.setC4RateFromGsPricing();
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get rates from GreenScreens',
						SNACKBAR.WARNING,
						SNACKBAR.ACTION
					);
					this.showGreenScreensCard = false;
					this.integrationPriceNotFound = true;
				}
			);
	}

	getSonarLanePricing() {
		this.sonarRate = null;
		this.isSonarRequestInProgress$.next(true);
		this.isC4RateSetFromIntegrationData$.next(false);
		this.showSonarCard = true;
		this.integrationPriceNotFound = false;
		const url = this.urlService.getSonarLanePricing();
		const searchTruckForm = this.pricingHelperService.retainSingleEquipment(
			this.searchTruckFormCopy
		);
		this.restService
			.POST(url, searchTruckForm)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isSonarRequestInProgress$.next(false);
					// markForCheck, not detectChanges: takeUntil completes the stream from
					// ngOnDestroy, which runs finalize while the view is being torn down, and
					// detectChanges throws on a destroyed view. Marking is enough on an OnPush
					// component -- the response is itself a zone event, so a check follows.
					this.cdRef.markForCheck();
				})
			)
			.subscribe(
				(result) => {
					this.sonarRate = result['data'];
					this.setC4RateFromSonarPricing();
				},
				(error: HttpErrorResponse) => {
					// 404 is mercury's answer for a lane Sonar holds no rate for -- equipment it
					// does not rate, a country outside its coverage -- which is an ordinary
					// outcome, not a fault. Toasting it would put a warning on the screen for
					// every such search. The empty state below says it either way.
					if (error?.status !== 404) {
						this.utilService.showSnackbar(
							'Unable to get rates from Sonar',
							SNACKBAR.WARNING,
							SNACKBAR.ACTION
						);
					}
					this.showSonarCard = false;
					this.integrationPriceNotFound = true;
				}
			);
	}

	/**
	 * The spot rate is the one that backfills C4's slots, never the contract rate: C4's own
	 * number is a spot buy rate, and a contract rate in its place would be a different kind of
	 * figure under the same label. A lane with only a contract rate leaves them empty.
	 */
	setC4RateFromSonarPricing() {
		if (this.unsupportedPricingCountry) {
			return;
		}
		const spotRate = this.sonarRate?.spot;
		if (spotRate && (this.priceNotFound || !this.showCurrentRatesCard)) {
			this.lanePricing = {
				allInCost: spotRate.highBuyRate,
				laneMiles: spotRate.distance,
				allInCostLow: spotRate.lowBuyRate,
				allInCostHigh: spotRate.highBuyRate,
				// Always null on the spot rate: Sonar publishes no fuel surcharge for it, because
				// its figures already include fuel. Set explicitly all the same, so the field is
				// cleared rather than left holding the previous search's value; the template
				// renders it as N/A.
				fuelCostPerMile: spotRate.fuelRate,
			};
			this.isC4RateSetFromIntegrationData$.next(true);
		}
	}

	setC4RateFromGsPricing() {
		// CN-8852: on a lane C4 cannot price we say so outright, so never backfill
		// C4's slots with Triumph's numbers -- Per Mile is derived from lanePricing
		// and would contradict the "Cargo Chief rate is not available" message sitting next
		// to it. Triumph's rate is shown in Triumph's own column, attributed.
		if (this.unsupportedPricingCountry) {
			return;
		}
		if (this.greenScreenRate && (this.priceNotFound || !this.showCurrentRatesCard)) {
			this.lanePricing = {
				allInCost: this.greenScreenRate?.highBuyRate,
				laneMiles: this.greenScreenRate?.distance,
				allInCostLow: this.greenScreenRate?.lowBuyRate,
				allInCostHigh: this.greenScreenRate?.highBuyRate,
				fuelCostPerMile: this.greenScreenRate?.fuelRate,
			};
			this.isC4RateSetFromIntegrationData$.next(true);
		}
	}

	convertDateToTimestamp() {
		this.searchTruckFormCopy.pickupDateEnd = GET_UNIX_TIMESTAMP(
			this.searchTruckFormCopy.pickupDateEnd
		);
		this.searchTruckFormCopy.pickupDateStart = GET_UNIX_TIMESTAMP(
			this.searchTruckFormCopy.pickupDateStart
		);
	}

	openRecentTransactionsDialog() {
		this.dialog.open(RecentTransactionsDialogComponent, {
			width: '900px',
			data: {
				transactions: this.recentTransactions,
				searchTruckForm: this.searchTruckFormCopy,
				accountId: this.accountId,
				recentTransactionsCount: this.recentTransactionsCount.totalTransactions,
				ageTransactionCount: this.agedTransactionCount,
			},
		});
	}

	//TODO: going ahead we should move this to a dumb component
	async submitRateFeedback(feedbackType: 'spotOn' | 'notAccurate', rateType: string = null) {
		this.rateFeedbackProvided = true;
		switch (feedbackType) {
			case 'spotOn':
				const spotOnFeedbackData = {
					rateType: 'spotOn',
					hasRate: true,
					currentRate: Math.round(this.lanePricing.allInCost),
				};
				this.pricingFeedbackService.firePricingSegmentEvent(
					SegmentEventsConstants.user_submitted_rate_feedback,
					'lanePricing',
					this.lanePricing,
					spotOnFeedbackData,
					this.truckSearchId,
					this.truckLaneSearchId,
					this.searchTruckForm,
					this.pricingFeedbackService.FEEDBACK_TYPE.SPOT_ON
				);
				this.pricingFeedbackService.submitLanePriceReview(
					this.lanePricing['lanePriceSearchId'],
					spotOnFeedbackData
				);
				this.pricingFeedbackService.showFeedbackSuccessSnackbar();
				break;
			case 'notAccurate':
				const dialogResponse = await this.pricingFeedbackService.openPricingFeedbackDialog({
					openedManually: true,
					rateType,
				});
				if (IS_NULL_OR_UNDEFINED(dialogResponse)) {
					this.rateFeedbackProvided = false;
					return;
				}
				let feedbackData = dialogResponse?.feedbackData;
				if (IS_NOT_NULL_OR_UNDEFINED(feedbackData)) {
					this.pricingFeedbackService.firePricingSegmentEvent(
						SegmentEventsConstants.user_submitted_rate_feedback,
						'lanePricing',
						this.lanePricing,
						feedbackData,
						this.truckSearchId,
						this.truckLaneSearchId,
						this.searchTruckForm,
						this.pricingFeedbackService.FEEDBACK_TYPE.INACCURATE
					);
					this.pricingFeedbackService.submitLanePriceReview(
						this.lanePricing['lanePriceSearchId'],
						feedbackData
					);
					this.pricingFeedbackService.showFeedbackSuccessSnackbar();
				}
				break;
		}
		this.pricingFeedbackSubmitted.emit();
	}

	renderRecentTransactions(): boolean {
		return (
			this.showRecentTransactionsCard &&
			IS_NOT_NULL_OR_UNDEFINED(this.recentTransactions) &&
			this.recentTransactions.length > 0
		);
	}

	getRecentTransactionsCount({ isSecondCall } = { isSecondCall: false }) {
		this.recentTransactionsCount = null;
		this.isRecentTransactionsCountRequestInProgress = true;
		this.searchTruckFormCopy = {
			...this.searchTruckFormCopy,
			originDeadhead: this.searchRadius || 100,
			destinationDeadhead: this.searchRadius || 100,
		};
		if (isSecondCall) {
			this.agedTransactionCount = true;
			this.searchTruckFormCopy = {
				...this.searchTruckFormCopy,
				age: environment.RECENT_TRANSACTION_AGE,
			};
		} else {
			this.recentTransactionsCount = null;
		}
		const url = this.urlService.getRecentTransactionsCount();
		this.restService
			.POST(url, this.searchTruckFormCopy)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isRecentTransactionsCountRequestInProgress = false;
					// The count is a plain field with no async pipe behind it, so on a lane
					// that skips C4 pricing nothing else marks the view dirty and the result
					// never reaches the DOM.
					this.cdRef.markForCheck();
				})
			)
			.subscribe(
				(result) => {
					if (result.data.totalTransactions) {
						this.recentTransactionsCount = result.data;
					} else if (!isSecondCall) {
						this.getRecentTransactionsCount({ isSecondCall: true });
					}
				},
				(error) => {
					let errorMessage = PRICING_ERROR.RECENT_TRANSACTION.DEFAULT;
					if (error.status === 422) {
						errorMessage = error.error;
					}
					this.utilService.showSnackbar(errorMessage, SNACKBAR.WARNING, SNACKBAR.ACTION);
					this.showRecentTransactionsCard = false;
				}
			);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}
}
